import { tracker } from '@ixigo/analytics';
import {
	funnelLauncher,
	type LoginConfig,
	LoginGate,
} from '@ixigo-packages/login';
import type { AuthState } from '@ixigo-packages/login/core';
import { AuthProvider, type LoginLauncher } from '@ixigo-packages/login/react';
import { useMemo } from 'react';
import { hasSyncedNativeSession } from '@/lib/pwa/app-data';
import type { AppContext } from '@/utils/headers';
import { globalTracker } from '@/utils/track';
import { getBrowserAuth } from './auth';
import {
	useAppContext,
	useMandatoryLogin,
	usePartnerLogin,
} from './useAppContext';

const loginConstants = {
	recaptchaToken: '6LcyoLcUAAAAAIawkLXQYgj61-aAhBT8HphqYI6Q',
	googleClientId:
		'609822318055-7gifp26me07tnaphkgu3ilf51hjusat6.apps.googleusercontent.com',
	resendTimer: 60,
};

export const getLoginConfig = (context: AppContext): LoginConfig => {
	const { recaptchaToken, resendTimer, googleClientId } = loginConstants;

	const clientConfig = context.clientConfig();
	const config: LoginConfig = {
		apiKey: clientConfig.apiKey,
		clientId: clientConfig.clientId,
		recaptchaToken,
		cookie: context.cookie,
		localStorage:
			typeof window !== 'undefined' ? window.localStorage : undefined,
		resendTimer,
		googleClientId,
		// Disabled: auto Google One-Tap fired the login/reCAPTCHA init on every
		// page load (incl. the marketing landing page), pulling in ~1MB of
		// gstatic reCAPTCHA before the user ever intends to log in. With this
		// off, reCAPTCHA loads lazily only when login is actually triggered.
		autoGoogleLogin: false,
		// Route funnel events through globalTracker so they carry the app-wide
		// commons (App ID / User ID / Device ID / Source) and fan out to CleverTap
		// + GA like every other event. Its `track` is typed `Partial<EventDataType>`,
		// which accepts the SDK's `undefined`-permitting payloads — no adapter.
		trackEvent: globalTracker.track,
		product: 'FINANCE',
		identifyCTLoggedInUser: tracker.identifyLoggedInUser,
		otplessEnabled: true,
	};
	return config;
};

export function LoginProvider({
	children,
	initialAuthState,
}: {
	children: React.ReactNode;
	initialAuthState?: AuthState;
}) {
	const browserAuth = getBrowserAuth();
	const context = useAppContext();
	// Mandatory-login routes (/_auth subtree, /apply) need a non-skippable funnel
	// — the 2.x `useLogin({ hideSkip: true })` behaviour. The 3.x `login()` takes
	// no per-call options, so drive `hideSkip` on the single global gate from the
	// active route instead.
	const hideSkip = useMandatoryLogin();
	// Partner account-linking surface (e.g. Swiggy webview): the funnel goes
	// mobile-OTP-only and loads the partner's CMS banner via its own product.
	const partner = usePartnerLogin();
	const config = useMemo(() => {
		if (!context) return null;
		const base = getLoginConfig(context);
		return partner ? { ...base, product: partner.loginProduct } : base;
	}, [context, partner]);
	// The SDK's funnelLauncher drives the mounted funnel; wrap it so the cached
	// cookie jar re-syncs before auth state is re-derived (login/logout), and
	// forward the autonomous one-tap settle signal so it reaches AuthProvider.
	const launcher = useMemo<LoginLauncher>(() => {
		const { subscribeLoginSettled } = funnelLauncher;
		return {
			login: async () => {
				const user = await funnelLauncher.login();
				context?.cookie.syncCookies();
				return user;
			},
			logout: async () => {
				await funnelLauncher.logout();
				context?.cookie.syncCookies();
			},
			subscribeLoginSettled: subscribeLoginSettled
				? (listener) =>
						subscribeLoginSettled(() => {
							context?.cookie.syncCookies();
							listener();
						})
				: undefined,
		};
	}, [context]);

	// LoginGate is light + eager; it idle-prefetches and lazy-mounts the heavy
	// funnel UI off the first-paint path. Imperative auth state + interceptor come
	// from AuthProvider, whose launcher opens the funnel on login().
	const tree = config ? (
		<LoginGate config={config} hideSkip={hideSkip} otpOnly={!!partner?.otpOnly}>
			{children}
		</LoginGate>
	) : (
		children
	);
	return (
		<AuthProvider
			auth={browserAuth}
			// The server cannot see a bridge-only session. Do not seed its stale
			// anonymous snapshot over the synchronized native session on hydration.
			initialState={
				hasSyncedNativeSession() ? browserAuth.getState() : initialAuthState
			}
			launcher={launcher}
			config={config ?? undefined}
		>
			{tree}
		</AuthProvider>
	);
}
